import {
    RequestForm,
    OfferForm,
    DeclineForm,
    UserBooking,
    BookingItineraryItem,
    BookingStatus,
    BookingInvoiceItem,
    UserSession,
    SessionStatus,
    ColorSetting,
    InstructorProfile,
    InstructorExperience,
    InstructorCertification,
    InstructorKudos,
    InstructorExperienceToVerify,
    SchoolOfferIncludedItem,
    InstructorJobOffer,
    InstructorInvitation,
    SchoolInstantBookingSettings,
    InstantOffer,
    BookingCommissionModel,
} from '../types';

export interface GetBooking {
    readonly userEmail?: string;
    readonly bookingId?: string;
    readonly bookingRef?: string;
}

export interface GetBookingResult {
    readonly booking?: UserBooking;
    readonly sessions?: UserSession[];
    readonly code:
    | 'SUCCESS'
    | 'BOOKING_NOT_FOUND';
}

export interface CreateBooking {
    name: string;
    email: string;
    tel: string;
    readonly recaptchaResponse: string;
    readonly requests: ReadonlyArray<CreateBookingRequest>;
}

export interface CreateInstantBooking {
    name: string;
    email: string;
    tel: string;
    readonly stripeToken: string;
    readonly instantOffers: ReadonlyArray<InstantOffer>;
}

export interface CreateBookingResult {
    readonly bookingId?: string;
    readonly email?: string;
    readonly code:
    | 'AUTHENTICATED_USER_EXISTS'
    | 'MISSING_BOOKING_EMAIL'
    | 'MISSING_BOOKING_NAME'
    | 'SUCCESS';
}

export interface ImportBookingResult {
    readonly bookingId?: string;
    readonly code:
    | 'MISSING_BOOKING_NAME'
    | 'SUCCESS';
}

export interface CreateBookingRequest extends Readonly<RequestForm> {
    readonly requestId: string;
    readonly schoolId: string;
}

export interface CancelBooking {
    readonly bookingId: string;
}

export interface RefreshBooking {
    readonly bookingId: string;
}

export interface MakeBookingOffer extends Readonly<OfferForm> {
    readonly bookingId: string;
    readonly schoolId: string;
    readonly requestId: string;
    readonly currency: string;
}

export interface ImportBooking {
    bookingName: string;
    bookingEmail: string;
    bookingTel: string;
    readonly schoolId: string;
    readonly requests: ReadonlyArray<CreateBookingRequest>;
    readonly offer: OfferForm;
    readonly status: BookingStatus;
}

export interface DeclineBooking extends Readonly<DeclineForm> {
    readonly bookingId: string;
    readonly schoolId: string;
}

export interface AcceptBookingOffers {
    readonly bookingId: string;
    readonly userId: string;
    readonly acceptedOffers: ReadonlyArray<BookingItineraryItem>;
}

export interface RejectBookingOffer {
    readonly bookingId: string;
    readonly offerId: string;
    readonly userId: string;
    readonly schoolId: string;
    readonly requestId: string;
    readonly reason: string;
}

export interface WithdrawBookingOffer {
    readonly bookingId: string;
    readonly offerId: string;
    readonly userId: string;
    readonly schoolId: string;
    readonly requestId: string;
}

export interface AddInvoice {
    readonly bookingId: string;
    readonly schoolId: string;
    readonly currency: string;
    readonly items: ReadonlyArray<BookingInvoiceItem>;
}

export interface MarkInvoicePaid {
    readonly bookingId: string;
    readonly schoolId: string;
    readonly invoiceId: string;
}

export interface PayForBooking {
    readonly bookingId: string;
    readonly schoolId: string;
    readonly stripeToken: string;
}

export interface SaveOfferSettings {
    readonly schoolId: string;
    readonly currency: string;
    readonly depositMin: number;
    readonly depositMax: number;
    readonly includedItems: { [type: string]: SchoolOfferIncludedItem };
    readonly instantBooking: SchoolInstantBookingSettings;
}
export interface SaveColorSettings {
    readonly schoolId: string;
    readonly bookingStatus: { [status: string]: ColorSetting };
}
export interface SaveCalendarSettings {
    readonly schoolId: string;
    readonly accessToken: string;
}

export interface SendBookingMessage {
    readonly messageId: string;
    readonly bookingId: string;
    readonly schoolId: string;
    readonly message: string;
}

export interface SaveSession {
    sessionId: string;
    schoolId: string;
    instructorEmails: string[];
    activity: string;
    format: string;
    type: string;
    members: SaveSessionMember[];
    start: string;
    durationHours: number;
    completedHours: number;
    spot: SaveSessionSpot | null;
    comments: string;
    status: SessionStatus;
}

export interface SaveSessionMember {
    bookingId: string;
    memberEmail: string;
    memberName: string;
}

export interface SaveSessionSpot {
    spotId: string;
    name: string;
    lat: number;
    lng: number;
}

export interface MoveSessions {
    schoolId: string;
    sessionIds: string[];
    laterByHours: number;
}

export interface GetInstructorProfile {
    readonly userId?: string;
    readonly accountName?: string;
}

export interface AddInstructorExperience {
    readonly experience: InstructorExperience[];
}

export interface AddInstructorCertifications {
    readonly certifications: InstructorCertification[];
}

export interface GiveInstructorKudos {
    readonly kudos: InstructorKudos;
}

export interface OfferInstructorJob {
    readonly jobOffer: InstructorJobOffer;
}

export interface InviteInstructor {
    readonly invitation: InstructorInvitation;
}

export interface GetInstructorProfileResult {
    readonly profile?: InstructorProfile;
    readonly code:
    | 'SUCCESS'
    | 'INSTRUCTOR_NOT_FOUND';
}

export interface VerifyInstructorExperience {
    experience: InstructorExperienceToVerify;
}

export interface GetInstantBookingSettings {
    readonly schoolIds: string[];
}

export interface GetInstantBookingSettingsResult {
    readonly settings: { [schoolId: string]: SchoolInstantBookingSettings };
    readonly commissionModels: { [schoolId: string]: BookingCommissionModel };
}

export const createRequestId = (schoolId: string, activity: string) => {
    return `${schoolId}-${activity}`;
};